import { openDepotDetail } from "./detail"
import { seriesLiqui, seriesSteuer } from "./series"
import { buildConfig } from "./config"
import { updateBewertungLinie, updateXAxisMinMax, updateDataGrouping } from "./helpers"
import { ChartConstants } from "../shared/constants"
import { depotDefaults } from "./defaults"

export function createDepotChart() {
	const obj = {
		// NUR ZUR DOKUMENTATION/INFO
		data: {
			// Liqui
			bruttowerte: [],
			aktien: [],
			anleihen: [],
			cashs: [],
			sonstiges: [],
			einzahlungen: [],
			auszahlungen: [],
			anlage_ueberliquiditaeten: [],
			automatische_liquidierungen: [],
			kosten: [],
			// Steuer
			kapitalertraege_privat: [],
			steuerbetraege_privat: [],
			kapitalertraege_unternehmen: [],
			steuerbetraege_unternehmen: [],
			// Sonstiges
			bewertungsdatum: 0,
			startdatum: 0,
			enddatum: 0,
			anzeigeformat: "jahr",
			chartType: "liqui",
			status: null,
			charteinstellungen: { hidden_series: [], bruttowert_ansicht: "konsolidiert" },
			id: null,
			detailUrl: null,
			detailFrameId: null,
		},

		// Zoom callbacks, settable by controller
		onZoomIn: () => {},
		onZoomReset: () => {},
		onLegendToggle: () => {},

		setZoomCallbacks({ onZoomIn, onZoomReset }) {
			if (typeof onZoomIn === "function") this.onZoomIn = onZoomIn
			if (typeof onZoomReset === "function") this.onZoomReset = onZoomReset
		},

		setChartSettingsCallbacks({ onLegendToggle }) {
			if (typeof onLegendToggle === "function") this.onLegendToggle = onLegendToggle
		},

		showLoading() {
			if (this.chart) this.chart.showLoading()
		},

		hideLoading() {
			if (this.chart) this.chart.hideLoading()
		},

		init() {
			const options = Highcharts.merge(depotDefaults(), buildConfig(this))
			this.chart = new Highcharts.stockChart("chart_depot", options)

			// Wenn der Chart Zeitraum kleiner ist als die Monatsgrenze das DataGrouping deaktivieren
			updateDataGrouping(this.chart, this.data.startdatum, this.data.enddatum)
		},

		update() {
			if (!this.chart) return

			this.chart.showLoading()

			const seriesArrays = [seriesLiqui(this.data), seriesSteuer(this.data)]
			const alleSeries = [].concat(...seriesArrays)

			alleSeries.forEach((serie) => {
				const s = this.chart.get(serie.id)
				if (!s) return

				// 1) Daten animiert aktualisieren
				s.setData(serie.data, false)

				// 2) showInLegend separat im Steuerchart aktualisieren
				if (s.options.showInLegend !== serie.showInLegend) {
					s.update({ showInLegend: serie.showInLegend }, false)
				}

				// 3. LegendName ändern wenn Steuerzuordnung wechselt
				if (s.options.legendName !== serie.legendName) {
					s.update({ legendName: serie.legendName, name: serie.name }, false)
				}
			})

			// xAchse: Min und Max aktualisieren
			updateXAxisMinMax(this)

			// Bewertungsline aktualisieren
			updateBewertungLinie(this)

			this.chart.redraw({ duration: ChartConstants.animationDuration })
			this.chart.hideLoading()
		},

		updateSeriesData(seriesData, chartType) {
			this.data.chartType = chartType
			// Alle Series entfernen
			while (this.chart.series.length > 0) {
				this.chart.series[0].remove(false)
			}

			// Series hinzufügen
			seriesData.forEach((serie) => {
				this.chart.addSeries(serie, false)
			})

			// XAchse Max Data prüfen und ggf. aktualisieren
			updateXAxisMinMax(this)
			updateBewertungLinie(this)
			this.chart.redraw({ duration: ChartConstants.animationDuration })
		},

		openDetailansicht(zeitpunkt) {
			if (!this.data.detailUrl || !this.data.detailFrameId) return

			const anzeigeformat = this.data.anzeigeformat
			const chartType = this.data.chartType || "liqui"

			openDepotDetail({
				url: this.data.detailUrl,
				zeitpunktUtcMs: zeitpunkt,
				anzeigeformat: anzeigeformat,
				chartType: chartType,
				frameId: this.data.detailFrameId,
			})
		},

		isSeriesHidden(seriesId) {
			const hs = this.data?.charteinstellungen?.hidden_series
			if (!Array.isArray(hs)) return false
			return hs.map(String).includes(String(seriesId))
		},

		visibleBySettings(seriesId, defaultVisible) {
			return this.isSeriesHidden(seriesId) ? false : defaultVisible
		},

		toggleHiddenSeriesLocal(seriesId, willBeVisible) {
			// willBeVisible == true  -> remove from hidden_series
			// willBeVisible == false -> add to hidden_series
			const id = String(seriesId)
			const settings = (this.data.charteinstellungen ||= {})
			const hs = Array.isArray(settings.hidden_series) ? settings.hidden_series.map(String) : []

			settings.hidden_series = hs

			if (willBeVisible) {
				settings.hidden_series = settings.hidden_series.filter((x) => x !== id)
			} else {
				if (!settings.hidden_series.includes(id)) settings.hidden_series.push(id)
			}
		},

		showAnlageklassenDifferenziert() {
			this.chart.update({
				series: [
					{ id: "bruttowerte", visible: this.visibleBySettings("bruttowerte", false), showInLegend: false },
					{ id: "aktien", visible: this.visibleBySettings("aktien", true), showInLegend: true },
					{ id: "anleihen", visible: this.visibleBySettings("anleihen", true), showInLegend: true },
					{ id: "cash", visible: this.visibleBySettings("cash", true), showInLegend: true },
					{ id: "sonstiges", visible: this.visibleBySettings("sonstiges", true), showInLegend: true },
				],
			})
		},

		showAnlageklassenKonsolidiert() {
			this.chart.update({
				series: [
					{ id: "bruttowerte", visible: this.visibleBySettings("bruttowerte", true), showInLegend: true },
					{ id: "aktien", visible: this.visibleBySettings("aktien", false), showInLegend: false },
					{ id: "anleihen", visible: this.visibleBySettings("anleihen", false), showInLegend: false },
					{ id: "cash", visible: this.visibleBySettings("cash", false), showInLegend: false },
					{ id: "sonstiges", visible: this.visibleBySettings("sonstiges", false), showInLegend: false },
				],
			})
		},
	}

	return obj
}
